/**
 * One place that turns a filter into human text — its LABEL and its
 * VALUE.
 *
 * Two formatters had grown for the same job: ``humanizeFilterValue`` in
 * the AI Analysis panel and the value text each picker computes inside
 * its own trigger. They disagreed (the AI panel showed ``hp_last_4_q``
 * with underscores; a picker showed the authored preset label) and
 * neither was translated — ``(All)`` / ``N selected`` were hardcoded
 * English even in the Spanish UI. A chip and the AI panel and the
 * picker trigger must all read the same string for the same value, so
 * that string is computed here once.
 *
 * The VALUE formatter is deliberately independent of the "is it active"
 * question: it renders whatever concrete value it is handed. Deciding
 * between a value and "All" belongs to ``filter-state``'s
 * ``isFilterActive`` — the caller combines the two.
 *
 * Only 5 of the engine's 11 filter types are used by any published
 * report today (select · date_range · autocomplete · tabs · search),
 * but every type is handled here with a plain-string fallback so a
 * future report can't render ``[object Object]``.
 */

import { groupFacetTags, parseFacetSelection } from "./facet-tags";
import type { Lang } from "./i18n-binding";
import { resolveLocalized, type DisplayLabel } from "./i18n-resolve";
import { formatRangeBound } from "./number-range";
import type { DashboardFilter, DatePreset } from "./types";

/** Minimal translator shape — matches ``react-i18next``'s ``t``. */
export type TranslateFn = (
  key: string,
  params?: Record<string, string | number>,
) => string;

export interface FilterFormatCtx {
  t: TranslateFn;
  locale: Lang;
  /** The max day of a ``date_calendar`` filter (its ``[min, max]``
   *  options), so the ``"latest"`` default reads as that day. */
  latestDay?: string;
}

/** ``date_calendar`` default meaning "the latest day with data" — mirror
 *  of ``report_engine.dashboard_engine.LATEST_DAY``. The report SQL
 *  resolves it; the UI shows the column's max day. */
export const LATEST_DAY = "latest";

export function isLatestDay(value: string | null | undefined): boolean {
  return (value ?? "").trim().toLowerCase() === LATEST_DAY;
}

/**
 * The only fields ``formatFilterValue`` reads. Typed as a ``Pick`` so a
 * caller that holds a narrow descriptor (the AI panel) doesn't have to
 * fabricate a ``column`` it never uses.
 */
export type FilterValueShape = Pick<
  DashboardFilter,
  "type" | "presets" | "option_labels" | "value_format" | "facet_tags"
>;

/** Mirror of ``first_page_cache._DATE_FILTER_TYPES`` (see filter-state). */
const DATE_FILTER_TYPES = new Set(["date_range", "date_relative", "date_slider"]);

// ── Label ─────────────────────────────────────────────────
// Moved verbatim from GenericFilterBar (were module-private there). A
// chip shows the label and nothing else, so it can no longer live
// inside the filter bar.

/** ``seller_status`` → "Seller Status". */
export function formatLabel(key: string): string {
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Display label for a filter, with a 3-tier fallback:
 *   1. explicit ``filt.label`` from the report config (may be bilingual);
 *   2. a ``filter.<key>`` i18n key, if the app translated it;
 *   3. ``formatLabel(filt.key)`` as the English default.
 *
 * Uses ``filt.key`` (a stable id) for the i18n lookup, not the
 * user-facing label — that is why this is separate from the generic
 * ``resolveLocalized`` used for KPI/chart/column labels where the raw
 * string IS the label.
 */
export function resolveFilterLabel(
  filt: DashboardFilter,
  t: TranslateFn,
  locale: Lang = "en",
): string {
  if (filt.label) return resolveLocalized(filt.label as DisplayLabel, locale);
  const translationKey = `filter.${filt.key}`;
  const translated = t(translationKey);
  if (translated !== translationKey) return translated;
  return formatLabel(filt.key);
}

// ── Value ─────────────────────────────────────────────────

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/**
 * Look up a date preset's authored label by id — so a chip shows
 * "Last 4 quarters" instead of the raw ``hp_last_4_q``. Falls back to
 * humanising the id when the report declares no matching preset.
 */
function presetText(
  presetId: string,
  presets: ReadonlyArray<DatePreset> | null | undefined,
  ctx: FilterFormatCtx,
): string {
  const match = presets?.find((p) => p.id === presetId);
  if (match?.label) {
    const resolved = resolveLocalized(match.label as DisplayLabel, ctx.locale);
    if (resolved) return resolved;
  }
  return presetId.replace(/_/g, " ");
}

function formatDateWindow(
  parsed: Record<string, unknown>,
  filt: FilterValueShape,
  ctx: FilterFormatCtx,
): string {
  if (typeof parsed["preset"] === "string") {
    return presetText(parsed["preset"], filt.presets, ctx);
  }
  const rel = parsed["relative"];
  if (isObject(rel)) {
    const length = Number(rel["length"]);
    const unit = String(rel["unit"] ?? "days").toLowerCase();
    if (Number.isFinite(length)) {
      const unitKey =
        unit === "weeks"
          ? "filterbar.relWeeks"
          : unit === "months"
            ? "filterbar.relMonths"
            : unit === "years"
              ? "filterbar.relYears"
              : "filterbar.relDays";
      return ctx.t(unitKey, { length });
    }
  }
  const start = parsed["start"];
  const end = parsed["end"];
  if (typeof start === "string" || typeof end === "string") {
    return `${start ?? ""} → ${end ?? ""}`.trim();
  }
  return "";
}

/**
 * Render a filter's ACTIVE value as human text. Returns ``""`` for an
 * absent / empty value — the caller decides whether that means "All".
 *
 * ``rawValue`` is what lives in ``activeFilters[key]`` (or, in the AI
 * panel, a loosely-typed ``unknown``). The type-specific decoders
 * mirror how ``dashboard_engine`` reads each value, so the chip text
 * never contradicts what the query actually did.
 */
/**
 * How many codes a ``sku_list`` value carries: the ``n`` of a saved
 * reference (``{"saved": id, "n": 118}``), the length of an expanded
 * ``{"codes": [...]}`` or bare list, ``null`` when the value says
 * nothing countable. Shared by the filter chip and the scope strip so
 * both say the same number and neither ever prints the reference.
 */
export function skuListCount(rawValue: unknown): number | null {
  if (typeof rawValue !== "string") return null;
  const parsed = safeParse(rawValue.trim());
  if (isObject(parsed)) {
    const n = parsed["n"];
    const codes = parsed["codes"];
    return typeof n === "number" ? n : Array.isArray(codes) ? codes.length : null;
  }
  if (Array.isArray(parsed)) return parsed.length;
  return null;
}

/**
 * Whether a ``sku_list`` value is a list UPLOADED with a saved filter —
 * a reference (``{"saved": id, "n": 118}``) or its expansion
 * (``{"codes": [...]}``) — rather than codes picked by hand, which are a
 * plain JSON list like any multi-select.
 */
export function isUploadedSkuList(rawValue: unknown): boolean {
  if (typeof rawValue !== "string" || !rawValue.trim()) return false;
  return isObject(safeParse(rawValue.trim()));
}

export function formatFilterValue(
  filt: FilterValueShape,
  rawValue: unknown,
  ctx: FilterFormatCtx,
): string {
  if (rawValue == null) return "";
  if (typeof rawValue === "number" || typeof rawValue === "boolean") {
    return String(rawValue);
  }
  if (typeof rawValue !== "string") {
    // Defensive: a non-string, non-scalar value should never reach a
    // chip, but never crash and never leak ``[object Object]``.
    const parsed = rawValue;
    return isObject(parsed) || Array.isArray(parsed) ? "" : String(parsed);
  }

  const trimmed = rawValue.trim();
  if (!trimmed) return "";

  const type = filt.type ?? "select";

  if (type === "date_calendar" && isLatestDay(trimmed)) {
    return ctx.latestDay || ctx.t("dashboard.scope.latest");
  }

  if (DATE_FILTER_TYPES.has(type)) {
    const parsed = safeParse(trimmed);
    if (isObject(parsed)) return formatDateWindow(parsed, filt, ctx);
    // Malformed / non-JSON date value: show it raw, never invent one.
    return trimmed;
  }

  if (type === "multi_select" && filt.facet_tags) {
    // One part per attribute ("Panel: QLED · Pantalla: 2 selected"), the
    // way the drawer shows them — never the raw "Attribute · Value" tags.
    return groupFacetTags(parseFacetSelection(trimmed))
      .map((g) => `${g.attribute}: ${
        g.values.length === 1 ? g.values[0]! : ctx.t("filterbar.nSelected", { n: g.values.length })
      }`)
      .join(" · ");
  }

  if (type === "multi_select" || type === "chip") {
    const parsed = safeParse(trimmed);
    if (Array.isArray(parsed)) {
      const items = parsed.filter((x) => x != null).map((x) => String(x));
      if (items.length === 0) return "";
      if (items.length === 1) return optionLabel(filt, items[0]!, ctx);
      return ctx.t("filterbar.nSelected", { n: items.length });
    }
    return trimmed;
  }

  if (type === "sku_list") {
    // Codes picked by hand are a plain JSON list: read like a
    // multi-select (the code itself, or "N selected").
    const picked = safeParse(trimmed);
    if (Array.isArray(picked)) {
      const items = picked.filter((x) => x != null).map((x) => String(x));
      if (items.length === 0) return "";
      if (items.length === 1) return items[0]!;
      return ctx.t("filterbar.nSelected", { n: items.length });
    }
    // DSA "Filtros guardados": the value is a reference to a saved
    // filter's uploaded list (``{"saved": id, "n": 118}``) — or, past the
    // portal's expansion, the codes themselves. Either way the chip says
    // how many codes narrow the report, never the codes.
    const count = skuListCount(trimmed);
    return count === null
      ? ctx.t("filterbar.skuList")
      : ctx.t("filterbar.skuListApplied", { n: count });
  }

  if (type === "range") {
    const parsed = safeParse(trimmed);
    if (isObject(parsed)) {
      const min = parsed["min"];
      const max = parsed["max"];
      const hasMin = typeof min === "number";
      const hasMax = typeof max === "number";
      // Bounds shown in the filter's display format (30 %, $2M); the
      // value itself stays in the column's units.
      const fmt = filt.value_format;
      if (hasMin && hasMax) {
        return `${formatRangeBound(min, fmt)} – ${formatRangeBound(max, fmt)}`;
      }
      if (hasMin) return ctx.t("filterbar.gte", { v: formatRangeBound(min, fmt) });
      if (hasMax) return ctx.t("filterbar.lte", { v: formatRangeBound(max, fmt) });
      return "";
    }
    return trimmed;
  }

  // select / autocomplete / tabs / search / date_calendar / anything
  // else — the value IS the display string, unless the filter authored
  // an ``option_labels`` entry for it.
  return optionLabel(filt, trimmed, ctx);
}

/** The authored label of an option value, or the value itself. */
function optionLabel(
  filt: FilterValueShape,
  value: string,
  ctx: FilterFormatCtx,
): string {
  const label = filt.option_labels?.[value];
  return label == null ? value : resolveLocalized(label, ctx.locale) || value;
}
